import { Injectable, inject } from '@angular/core';
import jsPDF, { GState } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  ContratoSplitCompleto, 
  FirmaElectronicaRegistro,
  normalizarRolSplitOficial 
} from '../models/splits.model';
import { SplitsCompilerService } from './splits-compiler.service';

@Injectable({
  providedIn: 'root'
})
export class SplitsPdfExporterService {
  private compiler = inject(SplitsCompilerService);

  construirDocumentoPdf(agregado: ContratoSplitCompleto, esVistaPrevia: boolean = false): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter'
    });

    const c = agregado.contrato;
    const colaboradores = (agregado.colaboradores || []).filter(col => !col.eliminado);
    const prod = this.compiler.obtenerProductorNormalizado(agregado);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let y = 15;

    const ensureSpace = (neededMm: number) => {
      if (y + neededMm > pageHeight - 18) {
        doc.addPage();
        y = 15;
      }
    };

    const writeParagraph = (text: string, fontSize: number = 8, isBold: boolean = false) => {
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(17, 24, 39);
      const lines = doc.splitTextToSize(text, contentWidth);
      const blockHeight = lines.length * (fontSize * 0.42) + 1.5;
      ensureSpace(blockHeight);
      doc.text(lines, margin, y);
      y += blockHeight;
    };

    const writeSectionTitle = (title: string) => {
      ensureSpace(12);
      y += 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(0, 0, 0);
      doc.text(title, margin, y);
      y += 3.5;
    };

    // ENCABEZADO OFICIAL IDÉNTICO A "Split Sheet.docx"
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text('SPLIT SHEET | ACUERDO DE PARTICIPACIONES', margin, y);
    y += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(17, 24, 39);
    doc.text('Obra musical · autorización de fijación · identificación del productor fonográfico', margin, y);
    y += 4.2;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(31, 41, 55);
    doc.text('MEDELLÍN MUSIC LAB', margin, y);
    y += 4.5;

    // 1. OBRA Y SESIÓN
    writeSectionTitle('1. Obra y sesión');

    const codigoSesion = c.codigoSesion || c.id;
    const fechaHora = c.fechaHoraSesion || c.fechaVigencia || new Date().toISOString().slice(0, 16).replace('T', ' ');
    const tituloObra = c.tituloObra?.trim() || '—';
    const estudioSede = c.estudioSede?.trim() || 'Medellín Music Lab';

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      body: [
        [
          { content: 'Código único de sesión', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
          { content: codigoSesion },
          { content: 'Fecha y hora', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
          { content: fechaHora }
        ],
        [
          { content: 'Título de la obra', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
          { content: tituloObra, styles: { fontStyle: 'bold' } },
          { content: 'Estudio / sede', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
          { content: estudioSede }
        ]
      ],
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7.8,
        cellPadding: 2,
        textColor: [17, 24, 39],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.20 },
        1: { cellWidth: contentWidth * 0.30 },
        2: { cellWidth: contentWidth * 0.22 },
        3: { cellWidth: contentWidth * 0.28 }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 4;

    // 2. PARTICIPANTES Y REPARTO DE LA OBRA MUSICAL
    writeSectionTitle('2. Participantes y reparto de la obra musical');
    writeParagraph(
      'El porcentaje corresponde únicamente a la participación convenida en los derechos patrimoniales de autor de la COMPOSICIÓN, no al fonograma, a la interpretación ni a los ingresos del máster.',
      7.8
    );

    const bodyParticipantes = colaboradores.length > 0
      ? colaboradores.map(col => {
          const rol = normalizarRolSplitOficial(col.rolContribucion);
          const aka = col.nombreArtistico?.trim() ? ` (${col.nombreArtistico.trim()})` : '';
          const contacto = [col.email?.trim(), col.telefono?.trim()].filter(Boolean).join(' · ');
          const identificacionCompleta = `${col.nombreCompleto || 'Por definir'}${aka}\n${col.tipoDocumento || 'CC'} ${col.numeroDocumento || 'Pendiente'}${contacto ? ' · ' + contacto : ''}`;

          return [
            identificacionCompleta,
            rol,
            col.ipi?.trim() || '—',
            `${Number(col.porcentajeSplit || 0).toFixed(2)} %`
          ];
        })
      : [['[Nombre completo, documento, contacto]', '[Rol creativo]', '[Opcional]', '0.00 %']];

    const sumaTotal = colaboradores.reduce((acc, col) => acc + (Number(col.porcentajeSplit || 0)), 0);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Participante / identificación', 'Rol creativo', 'IPI (si tiene)', '% autoría']],
      body: bodyParticipantes,
      foot: [[
        { content: 'TOTAL', styles: { fontStyle: 'bold' } },
        { content: '' },
        { content: '' },
        { content: `${sumaTotal.toFixed(2)} %`, styles: { halign: 'right', fontStyle: 'bold' } }
      ]],
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7.8,
        cellPadding: 2,
        textColor: [17, 24, 39],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8
      },
      footStyles: {
        fillColor: [248, 250, 252],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.37 },
        1: { cellWidth: contentWidth * 0.33 },
        2: { cellWidth: contentWidth * 0.13, halign: 'center' },
        3: { cellWidth: contentWidth * 0.17, halign: 'right', fontStyle: 'bold' }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 3;

    writeParagraph(
      'Las partes acuerdan que las participaciones indicadas representan el 100 % de la titularidad patrimonial de autor que reconocen entre los firmantes sobre esta obra. Dichas participaciones son convenidas libremente por los firmantes y pueden reconocerse a cualquier participante, incluido el productor musical, con independencia de su rol en la sesión. Los derechos morales permanecen en cabeza de sus autores conforme a la ley aplicable.',
      7.6
    );

    // 3. DESIGNACIÓN DEL PRODUCTOR FONOGRÁFICO
    writeSectionTitle('3. Designación del productor fonográfico');

    const selLinea = prod.seleccion === 'COPRODUCTORES'
      ? 'Selección obligatoria:   ( ) Participante de esta sesión    (•) Coproductores (identificar a todos).'
      : 'Selección obligatoria:   (•) Participante de esta sesión    ( ) Coproductores (identificar a todos).';
    writeParagraph(selLinea, 7.8, true);

    const prodNombre = this.compiler.obtenerNombreProductorDeclarado(prod, colaboradores);
    const prodDoc = this.compiler.obtenerDocumentoProductorDeclarado(prod, colaboradores);
    const prodCorreo = this.compiler.obtenerCorreoProductorDeclarado(prod, colaboradores);
    const modalidadTexto = prod.modalidad === 'COPRODUCTORES'
      ? '( ) Productor único    (•) Coproductores (identificar a todos y documentar su participación)'
      : '(•) Productor único    ( ) Coproductores (identificar a todos y documentar su participación)';

    const filasProd: any[] = [
      [
        { content: 'Nombre completo', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
        { content: prodNombre }
      ],
      [
        { content: 'Tipo y número de identificación', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
        { content: prodDoc }
      ],
      [
        { content: 'Correo para aceptación', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
        { content: prodCorreo }
      ],
      [
        { content: 'Modalidad', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
        { content: modalidadTexto }
      ]
    ];

    if (prod.modalidad === 'COPRODUCTORES') {
      const listaCoprod = this.compiler.obtenerListaCoproductores(prod, colaboradores);
      if (listaCoprod.length > 0) {
        const detalle = listaCoprod
          .map(item => `${item.nombreCompleto} (${item.tipoDocumento} ${item.numeroDocumento} · ${item.correo})`)
          .join(' | ');
        filasProd.push([
          { content: 'Coproductores registrados', styles: { fontStyle: 'bold', fillColor: [248, 250, 252] } },
          { content: detalle }
        ]);
      }
    }

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      body: filasProd,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7.8,
        cellPadding: 2,
        textColor: [17, 24, 39],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.36 },
        1: { cellWidth: contentWidth * 0.64 }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 3;

    writeParagraph(
      `Los participantes que suscriben el presente Split Sheet, de manera libre, expresa e informada, aceptan y reconocen que ${prodNombre}, identificado(a) con ${prodDoc}, es el (la) PRODUCTOR(A) FONOGRÁFICO(A) del fonograma resultante de esta sesión de grabación y, en consecuencia, titular exclusivo(a) de los derechos patrimoniales sobre dicho fonograma (MÁSTER). En esa condición podrá reproducirlo, distribuirlo, comercializarlo, comunicarlo públicamente, ponerlo a disposición del público, licenciarlo y explotarlo económicamente, directamente o por terceros, en cualquier territorio y por cualquier medio, durante el término de protección legal.`,
      7.4
    );

    // 4. AUTORIZACIONES DE LOS PARTICIPANTES GRABADOS
    writeSectionTitle('4. Autorizaciones de los participantes grabados');
    writeParagraph('Cada persona con voz o interpretación/ejecución incorporada al audio responde individualmente:', 7.8);

    const bodyAutorizaciones = colaboradores.length > 0
      ? colaboradores.map(col => {
          const autStr = col.autorizaFijacion === 'NO'
            ? '( ) Sí    (•) No'
            : '(•) Sí    ( ) No';

          return [
            col.nombreCompleto || 'Participante',
            autStr
          ];
        })
      : [['[Nombre autocompletado]', '( ) Sí    ( ) No']];

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Participante', '¿Autoriza la fijación, edición, mezcla, masterización y archivo técnico de su interpretación o ejecución, así como su reproducción, distribución, comunicación pública y puesta a disposición incorporada en el fonograma?']],
      body: bodyAutorizaciones,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7.8,
        cellPadding: 2,
        textColor: [17, 24, 39],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7.5
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.38 },
        1: { cellWidth: contentWidth * 0.62, halign: 'center' }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 3;

    writeParagraph(
      'Cada participante cuya voz, interpretación o ejecución haya quedado incorporada en la grabación debe responder individualmente la autorización anterior. La respuesta afirmativa autoriza al (la) productor(a) fonográfico(a) a explotar dicha interpretación o ejecución en los términos de la sección 3. Si alguna persona marca «No», su intervención no podrá utilizarse mientras no exista autorización válida o se retire del fonograma.',
      7.4
    );

    // 5. ACEPTACIÓN Y FIRMA ELECTRÓNICA
    writeSectionTitle('5. Aceptación y firma electrónica');
    writeParagraph(
      'Cada firmante confirma sus datos, aporte y porcentaje de derechos patrimoniales de autor cuando corresponda; acepta expresamente la designación del productor fonográfico identificado en la sección 3 y las autorizaciones individuales que haya otorgado. Las partes manifiestan que han leído y aceptan libremente este documento. El productor fonográfico designado debe firmar para dejar constancia de su aceptación y de la condición declarada. Con la firma del presente Split Sheet, cada firmante declara que su aporte es original y no infringe derechos de terceros.',
      7.5
    );

    const firmasRowsMeta: { firma?: FirmaElectronicaRegistro }[] = [];

    const bodyFirmas: any[] = colaboradores.map((col, idx) => {
      const rol = normalizarRolSplitOficial(col.rolContribucion);
      const f = col.firma;
      firmasRowsMeta.push({ firma: f });

      const col1 = `Participante ${idx + 1}: ${col.nombreCompleto || 'Por definir'}\n${col.tipoDocumento || 'CC'} ${col.numeroDocumento || '—'} · ${rol} · ${Number(col.porcentajeSplit || 0).toFixed(2)} %`;
      const col2 = f?.firmado
        ? f.firmaDataUrl
          ? `[X] Aceptación expresa confirmada\n\n\n`
          : `[X] Aceptación expresa confirmada\nFirma electrónica: ${f.firmaTexto || col.nombreCompleto}`
        : `[ ] Pendiente de aceptación y firma\n\n_______________________________`;
      const col3 = f?.firmado
        ? `Fecha: ${f.fechaFirma ? new Date(f.fechaFirma).toLocaleString('es-CO') : 'Registrada'}\nSello: ${f.hashVerificacion || 'MML'}\n${f.trazabilidadTexto || 'Firmado en Medellín Music Lab'}`
        : 'Pendiente de firma / sello de tiempo';

      return [col1, col2, col3];
    });

    // Fila del productor fonográfico designado
    const fProd = prod.firmaProductor;
    firmasRowsMeta.push({ firma: fProd });
    bodyFirmas.push([
      `Productor fonográfico designado:\n${prodNombre}\n${prodDoc}`,
      fProd?.firmado
        ? fProd.firmaDataUrl
          ? `[X] Aceptación expresa confirmada\n\n\n`
          : `[X] Aceptación expresa confirmada\nFirma electrónica: ${fProd.firmaTexto || prodNombre}`
        : `[ ] Pendiente de aceptación expresa y firma\n\n_______________________________`,
      fProd?.firmado
        ? `Fecha: ${fProd.fechaFirma ? new Date(fProd.fechaFirma).toLocaleString('es-CO') : 'Registrada'}\nSello: ${fProd.hashVerificacion || 'MML'}\n${fProd.trazabilidadTexto || 'Aceptación de productor fonográfico'}`
        : 'Pendiente de firma / sello de tiempo'
    ]);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Firmante / calidad', 'Aceptación y firma', 'Fecha y trazabilidad']],
      body: bodyFirmas,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7.5,
        cellPadding: 2,
        minCellHeight: 15,
        textColor: [17, 24, 39],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.35 },
        1: { cellWidth: contentWidth * 0.34 },
        2: { cellWidth: contentWidth * 0.31 }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const meta = firmasRowsMeta[data.row.index];
          if (meta?.firma?.firmado && meta.firma.firmaDataUrl) {
            try {
              doc.addImage(
                meta.firma.firmaDataUrl,
                'PNG',
                data.cell.x + 3,
                data.cell.y + 5.5,
                34,
                8.5
              );
            } catch {
              // Si falla el renderizado de imagen base64, el texto de aceptación ya está impreso
            }
          }
        }
      }
    });

    y = (doc as any).lastAutoTable.finalY + 3;

    writeParagraph(
      'Medellín Music Lab facilita el diligenciamiento y conserva el registro; no adquiere derechos por esa sola intervención. El monitor solo verifica que el flujo de diligenciamiento se haya completado, sin certificar titularidades ni asesorar individualmente a las partes.',
      7.4
    );

    // PIE DE PÁGINA OFICIAL ("Medellín Music Lab  |  Split Sheet digital") Y MARCA DE AGUA
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);

      if (esVistaPrevia) {
        doc.saveGraphicsState();
        try {
          const gs = new GState({ opacity: 0.10 });
          doc.setGState(gs);
        } catch {
          // Fallback
        }
        doc.setTextColor(148, 163, 184);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(44);
        doc.text('VISTA PREVIA', pageWidth / 2, 140, { align: 'center', angle: 45 });
        doc.restoreGraphicsState();
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(71, 85, 105);
      doc.text(
        'Medellín Music Lab  |  Split Sheet digital',
        pageWidth / 2,
        271,
        { align: 'center' }
      );
      doc.setFontSize(6.8);
      doc.text(`Página ${p} de ${totalPages}`, pageWidth - margin, 271, { align: 'right' });
    }

    return doc;
  }

  exportarPdf(agregado: ContratoSplitCompleto): void {
    const doc = this.construirDocumentoPdf(agregado, false);
    const contrato = agregado.contrato;
    const safeTitle = (contrato.tituloObra || 'split-sheet')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '_');
    doc.save(`Split_Sheet_MML_${safeTitle}_${contrato.fechaVigencia}.pdf`);
  }

  generarPdfBlobUrl(agregado: ContratoSplitCompleto, esVistaPrevia: boolean = true): string {
    const doc = this.construirDocumentoPdf(agregado, esVistaPrevia);
    const blob = doc.output('blob');
    return URL.createObjectURL(blob);
  }

  generarPdfDataUri(agregado: ContratoSplitCompleto): { dataUri: string; sizeBytes: number } {
    const doc = this.construirDocumentoPdf(agregado, false);
    const arrayBuffer = doc.output('arraybuffer');
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return {
      dataUri: `data:application/pdf;base64,${base64}`,
      sizeBytes: bytes.byteLength
    };
  }

  generarPdfBlob(
    agregado: ContratoSplitCompleto,
    nombrePersonalizado?: string
  ): { blob: Blob; file: File; nombreArchivo: string; sizeBytes: number } {
    const doc = this.construirDocumentoPdf(agregado, false);
    const arrayBuffer = doc.output('arraybuffer');
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
    const contrato = agregado.contrato;
    const safeTitle = (contrato.tituloObra || 'split-sheet')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '_');
    const nombreArchivo =
      nombrePersonalizado || `Split_Sheet_MML_${safeTitle}_${contrato.fechaVigencia || 'oficial'}.pdf`;
    const file = new File([blob], nombreArchivo, { type: 'application/pdf' });
    return {
      blob,
      file,
      nombreArchivo,
      sizeBytes: blob.size
    };
  }
}
